import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import type { ExperienceDetail } from "@/mocks/experienceDetails";

const steps = [
  {
    icon: "ri-calendar-line",
    title: "Choose your date",
    text: "Pick a preferred weekday and group size — weekends and harvest weeks fill up fast.",
  },
  {
    icon: "ri-chat-check-line",
    title: "We confirm availability",
    text: "Our team checks the farm's calendar and confirms your slot over WhatsApp or email.",
  },
  {
    icon: "ri-plant-line",
    title: "Arrive & enjoy",
    text: "Turn up ready to get your hands dirty. Everything else is arranged for you.",
  },
];

export default function ReserveSection({ detail }: { detail: ExperienceDetail }) {
  return (
    <section id="reserve" className="sun-glaze bg-background-100 py-20 md:py-28">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <Reveal className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
              <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                Reserve
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
              Reserve Your Date for {detail.title}
            </h2>
            <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
              Farm stays run on the seasons, not the clock — so a quick date check goes a long way.
              Send us your preferred date and we will hold your spot with the farming family.
            </p>

            <div className="mt-9 space-y-6">
              {steps.map((step, index) => (
                <div key={step.title} className="flex items-start gap-4">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-secondary-500 text-background-50 shrink-0">
                    <i className={`${step.icon} text-xl`}></i>
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500">
                      Step {index + 1}
                    </p>
                    <h3 className="font-heading text-lg text-foreground-950 font-semibold mt-0.5">
                      {step.title}
                    </h3>
                    <p className="text-sm text-foreground-700 leading-relaxed mt-1">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={120}>
            <div
              id="booking-calendar"
              className="bg-background-50 rounded-lg border border-background-200 p-7 md:p-9 lg:sticky lg:top-24"
            >
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                <i className="ri-calendar-check-line text-2xl"></i>
              </span>
              <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
                Good to Know
              </h3>
              <p className="text-sm text-foreground-700 leading-relaxed mt-3">
                Reserving {detail.title} is done through the enquiry form just below — share your
                preferred date and group size, and we will confirm availability with the farming
                family.
              </p>

              <ul className="mt-7 space-y-3 border-t border-background-200 pt-6">
                {[
                  "Free to reserve — no payment now",
                  "Reply within 24 hours",
                  "Free to reschedule with notice",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                      <i className="ri-check-line text-sm"></i>
                    </span>
                    <span className="text-sm text-foreground-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}